/**
 * views/ForgotPassword.tsx — /forgot-password
 * ────────────────────────────────────────────
 * Unauthenticated route.  Pilot enters their email; the backend generates a
 * short-lived (15 min) reset token and delivers the link via SMTP (if
 * configured) or logs it for the admin to deliver manually.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient, ApiError } from '@/api/client';
import { InfiniumWordmark } from '@/components/ui/InfiniumLogo';

export default function ForgotPassword() {
  const [email, setEmail]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [sent, setSent]         = useState(false);
  const [error, setError]       = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setError(null);
    try {
      await apiClient.post('/api/auth/forgot-password', { email: email.trim() }, { skipAuth: true });
      setSent(true);
    } catch (err) {
      // The endpoint always returns 200; if we get here it's a network error.
      setError(err instanceof ApiError ? err.message : 'Could not reach the server. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-brand-logo">
            <InfiniumWordmark variant="light" height={110} />
          </div>
          <h1 className="login-brand-name">Forgot Password</h1>
          <p className="login-brand-sub">Enter your email to receive a reset link</p>
        </div>

        {sent ? (
          <div className="auth-sent-box">
            <div className="auth-sent-icon">✉</div>
            <p className="auth-sent-msg">
              If that email is registered, a reset link has been sent.
              Check your inbox or ask your administrator.
            </p>
            <Link to="/" className="auth-back-link">Back to sign in</Link>
          </div>
        ) : (
          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <div className="form-field">
              <label className="form-label" htmlFor="fp-email">Email</label>
              <input
                id="fp-email"
                type="email"
                className="form-input"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={busy}
                required
              />
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}

            <button
              type="submit"
              className="login-submit-btn"
              disabled={busy || !email.trim()}
            >
              {busy ? 'Sending…' : 'Send reset link'}
            </button>

            <Link to="/" className="auth-back-link">Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
}
